import React from 'react'
import { ContentLabel } from '../../../../components/ContentLabel'
import styles from './FirmDashboardOverviewSidebar.sass'
import Icon, { IconName } from '../../../../components/Icon'
import {
  Section,
  SectionContent,
  SectionHeader,
  useSection,
} from '../../../../components/Section'
import OverviewSidebarViewModel from './FirmDashboardOverviewSidebarViewModel'
import { useActivation } from '../../../../hooks/mobx-hooks'
import { observer } from 'mobx-react'
import InfiniteScrollLoader from '../../../../components/InfiniteScrollLoader'
import ScrollView from '../../../../components/ScrollView'
import SearchableJob from '../../../SearchableJob'
import Avatar from '../../../../components/Avatar'
import Link from '../../../../components/Link'
import link from '../../../../infra/link'
import { Id } from '../../../../types/definitions'
import { formatMoney } from '../../../../utils/money-util'
import ContentLoading from '../../../../components/ContentLoading'
import ContentError from '../../../../components/ContentError'
import { timeago } from '../../../../utils/date-util'
import cx from 'classnames'
import { match } from '../../../../utils/pattern-match-util'
import { PhaseOfDay } from '../../../../utils/greeting-util'
import {
  Dropdown,
  DropdownItemDescription,
  DropdownItemTitle,
} from 'components/Dropdown'
import { IconButton } from 'components/IconButton'
import Tooltip from 'components/Tooltip'

interface OverviewSidebarProps {
  vm: OverviewSidebarViewModel
}

export default observer(function FirmDashboardOverviewSidebar({
  vm,
}: OverviewSidebarProps) {
  useActivation(vm)

  return (
    <>
      <ContentLabel>Overview</ContentLabel>

      <div className={styles.overviewContainer}>
        {vm.activate.match({
          pending: () => <ContentLoading />,
          rejected: (err) => <ContentError error={err} />,
          resolved: () => <OverviewSidebar vm={vm} />,
        })}
      </div>
    </>
  )
})

const OverviewSidebar = observer(function OverviewSidebar({
  vm,
}: OverviewSidebarProps) {
  return (
    <>
      <Header vm={vm} />
      <JobList vm={vm} />
    </>
  )
})

interface OverviewAmountItemProps {
  amount: number
  icon: IconName
  title: React.ReactNode
}
const OverviewContractItem = observer(
  ({ amount, icon, title }: OverviewAmountItemProps) => (
    <div className={styles.contractItem}>
      <Icon name={icon} className={styles.contractIcon} />
      <h2 className={styles.contractTitle}>{formatMoney(amount)}</h2>
      <p className={styles.contractType}>{title}</p>
    </div>
  ),
)

interface ContractUsageItemProps {
  vm: OverviewSidebarViewModel
}

const ContractUsageItem = observer(({ vm }: ContractUsageItemProps) => (
  <div className={styles.contractItem}>
    <Icon name={'analytics'} className={styles.contractIcon} />
    <h2 className={styles.contractTitle}>{formatMoney(vm.contractUsage)}</h2>
    <div className={styles.feesDropdown}>
      <p className={styles.contractType}>Contract usage</p>
      <Dropdown
        position="bottom"
        button={
          <IconButton>
            <Icon size={16} name="dropdown" />
          </IconButton>
        }
      >
        <div className={styles.contractBreakdown}>
          <div className={styles.contractBreakdownItem}>
            <DropdownItemTitle>Submitted jobs</DropdownItemTitle>
            <DropdownItemDescription>
              {formatMoney(vm.jobsTotalPricing.submittedJobsTotalPrice)}
            </DropdownItemDescription>
          </div>
          <div className={styles.contractBreakdownItem}>
            <DropdownItemTitle>Closed jobs</DropdownItemTitle>
            <DropdownItemDescription>
              {formatMoney(vm.jobsTotalPricing.closedJobsTotalPrice)}
            </DropdownItemDescription>
          </div>
          {vm.contractInfo.setupFees > 0 && (
            <div className={styles.contractBreakdownItem}>
              <DropdownItemTitle>Setup fee</DropdownItemTitle>
              <DropdownItemDescription>
                {formatMoney(vm.contractInfo.setupFees)}
              </DropdownItemDescription>
            </div>
          )}
          {vm.contractInfo.vmFees > 0 && (
            <div className={styles.vmFeeBreakdown}>
              <div>
                <DropdownItemTitle>Virtual machines</DropdownItemTitle>
                <DropdownItemDescription>
                  {formatMoney(vm.contractInfo.vmFees)}
                </DropdownItemDescription>
              </div>
              <VmFeesDropdown vm={vm} />
            </div>
          )}
        </div>
      </Dropdown>
    </div>
  </div>
))

interface VmFeesDropdownProps {
  vm: OverviewSidebarViewModel
}

const VmFeesDropdown = observer(({ vm }: VmFeesDropdownProps) => {
  return (
    <Dropdown
      position="right-start"
      button={
        <IconButton>
          <Icon color="var(--brand-primary)" size={24} name="arrow" />
        </IconButton>
      }
    >
      <div className={styles.contractBreakdown}>
        <div className={styles.contractBreakdownItem}>
          <DropdownItemTitle>Quantity</DropdownItemTitle>
          <DropdownItemDescription>
            {vm.contractInfo.vmFeesQuantity}
          </DropdownItemDescription>
        </div>
        <div className={styles.contractBreakdownItem}>
          <DropdownItemTitle>Individual price</DropdownItemTitle>
          <DropdownItemDescription>
            {formatMoney(vm.contractInfo.vmFeesAmountPerUnit)}
          </DropdownItemDescription>
        </div>
      </div>
    </Dropdown>
  )
})

interface OverviewContractProps {
  children: React.ReactNode
}

const OverviewContract = observer(function OverviewContract({
  children,
}: OverviewContractProps) {
  return <div className={styles.contract}>{children}</div>
})

const usDateFormatter = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
})

interface ContractDateProps {
  date: Date
  title: string
  align?: 'right' | 'left'
}

const ContractDate = observer(function ContactDate({
  date,
  title,
  align,
}: ContractDateProps) {
  return (
    <div className={cx(styles.contractDateContainer, align && styles[align])}>
      <div className={styles.contractDate}>{usDateFormatter.format(date)}</div>
      <div className={styles.contractDateTitle}>{title}</div>
    </div>
  )
})

interface ContractDateRangeProps {
  dateStart: Date | null
  dateEnd: Date | null
}

const ContractDateRange = observer(function ContractDateRange({
  dateEnd,
  dateStart,
}: ContractDateRangeProps) {
  if (!dateEnd || !dateStart) {
    return <></>
  }

  return (
    <div>
      <div className={styles.contractSeparator} />
      <div className={styles.contractDateRange}>
        <ContractDate date={dateStart} title="Contract start date" />
        <Icon name="back" rotate={180} color="var(--grey500)" size={24} />
        <ContractDate date={dateEnd} title="Contract end date" align="right" />
      </div>
    </div>
  )
})

interface OverviewSidebarHeaderProps {
  vm: OverviewSidebarViewModel
}

const Header = observer(({ vm }: OverviewSidebarHeaderProps) => (
  <div className={styles.header}>
    <h1 className={styles.title}>
      {match(vm.phaseOfDay, {
        [PhaseOfDay.Morning]: () => `Good morning, ${vm.userName}!`,
        [PhaseOfDay.Afternoon]: () => `Good afternoon, ${vm.userName}!`,
        [PhaseOfDay.Evening]: () => `Good evening, ${vm.userName}!`,
      })}
    </h1>
    <p className={styles.description}>
      Here is an overview of your contract, along with pending items that need
      your attention.
    </p>
    <OverviewContract>
      <OverviewContractItem
        amount={vm.jobsTotalPricing.submittedJobsTotalPrice}
        icon="jobs"
        title={
          <div className={styles.submittedJobsTooltip}>
            <span>Submitted jobs</span>{' '}
            <Tooltip content="Jobs In Progress" placement="top">
              <Icon name="info" size={20} color="var(--brand-primary)" />
            </Tooltip>
          </div>
        }
      />
      <OverviewContractItem
        amount={vm.jobsTotalPricing.closedJobsTotalPrice}
        icon="payout"
        title="Completed Jobs"
      />
      {vm.canViewContractAmount && (
        <>
          <ContractUsageItem vm={vm} />
          <OverviewContractItem
            amount={vm.contractInfo.contractTotal}
            icon="documents"
            title="Contract total"
          />
        </>
      )}
    </OverviewContract>

    {vm.contractInfo.isContractConfigEnabled && (
      <ContractDateRange
        dateStart={vm.contractInfo.contractStartDate}
        dateEnd={vm.contractInfo.contractEndDate}
      />
    )}
  </div>
))

const JobList = observer(({ vm }: OverviewSidebarProps) => {
  const scrollRef = React.useRef<HTMLDivElement | null>(null)
  const scrollProvider = vm.searcher.scroller
  const openItemsSection = useSection(false)
  const draftReviewItemsSection = useSection(false)

  return (
    <div className={styles.body}>
      <ScrollView ref={scrollRef}>
        <Section {...openItemsSection}>
          <SectionHeader>
            Open items
            <span className={styles.jobCount}>
              &nbsp;({vm.openItemsJobsCount})
            </span>
          </SectionHeader>
          <SectionContent>
            {vm.openItemsJobs.map((job) => (
              <Job key={job.id} job={job} />
            ))}
          </SectionContent>
        </Section>

        <Section {...draftReviewItemsSection}>
          <SectionHeader>
            Draft review
            <span className={styles.jobCount}>
              &nbsp;({vm.draftReviewJobsCount})
            </span>
          </SectionHeader>

          <SectionContent>
            {vm.draftInReviewJobs.map((job) => (
              <Job key={job.id} job={job} />
            ))}
          </SectionContent>
        </Section>

        <InfiniteScrollLoader compact scrollProvider={scrollProvider} />
      </ScrollView>
    </div>
  )
})

interface JobProps {
  job: SearchableJob
}

const Job = observer(({ job }: JobProps) => {
  const subjects =
    job.currentUserMember?.type === 'CLIENT' && job.proMembers.length
      ? job.proMembers
      : job.clientMembers

  let lastUpdate = timeago(job.dateModified ?? job.dateCreated, 'long')

  if (lastUpdate !== 'now') {
    lastUpdate = `${lastUpdate} ago`
  }

  return (
    <Link className={styles.item} to={link.job(Id.assert(job.id))}>
      <div className={styles.avatarContainer}>
        <Avatar size={32} subject={subjects} />
      </div>

      <div className={styles.primaryInfo}>
        <span className={styles.primaryText}>{job.displayName}</span>
        <span className={styles.secondaryInfo}>{lastUpdate}</span>
      </div>
    </Link>
  )
})
